import { useCallback, useEffect } from "react";
import { isAxiosError } from "axios";
import { supabase } from "@/lib/supabase";
import { api, type ApiResponse } from "@/lib/api";
import { useAuthStore } from "@/stores/authStore";
import { clearUpgradeDismissals } from "@/hooks/useUpgradeDismissal";
import { toast } from "@/stores/toastStore";
import type { AuthSession } from "@ridereserv/shared/types";

/**
 * Surface the one-time "we linked your past rides" toast when a session fetch
 * reports newly auto-linked guest reservations (epic #605). The API only returns
 * a non-null summary on the fetch that performed the link, so this fires at most
 * once per actual linking event; the toast store also de-dupes identical
 * messages, so calling it from every session-fetch site is safe.
 * @param session - The session payload returned by `/auth/session`
 */
export function surfaceLinkedRides(session: AuthSession): void {
  const summary = session.linkedRideSummary;
  if (!summary || summary.newlyLinked <= 0) return;
  const n = summary.newlyLinked;
  toast.success(
    n === 1
      ? "We found 1 past ride and added it to your account."
      : `We found ${String(n)} past rides and added them to your account.`
  );
}

/**
 * Main auth hook for accessing auth state and actions.
 * @returns The auth state object
 */
export function useAuth() {
  const store = useAuthStore();

  return {
    user: store.user,
    memberships: store.memberships,
    isAuthenticated: store.isAuthenticated,
    isLoading: store.isLoading,
  };
}

/**
 * Hook for authentication actions (login, logout, register).
 * @returns The auth action functions
 */
export function useAuthActions() {
  const { setAuth, clearAuth, setLoading } = useAuthStore();

  const login = useCallback(
    async (email: string, password: string) => {
      setLoading(true);
      try {
        // Sign in with Supabase
        const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
          email,
          password,
        });

        if (authError) {
          throw new Error(authError.message);
        }

        const { session } = authData;

        // Fetch user profile and memberships from our API
        const response = await api.get<ApiResponse<AuthSession>>("/auth/session", {
          headers: {
            Authorization: `Bearer ${session.access_token}`,
          },
        });

        if (!response.data.success || !response.data.data) {
          throw new Error("Failed to fetch user session");
        }

        const authSession = response.data.data;
        setAuth(authSession.user, authSession.memberships);
        surfaceLinkedRides(authSession);

        return { success: true };
      } catch (error) {
        setLoading(false);
        const message = error instanceof Error ? error.message : "Login failed";
        return { success: false, error: message };
      }
    },
    [setAuth, setLoading]
  );

  const register = useCallback(
    async (email: string, password: string, name: string, phone?: string) => {
      setLoading(true);
      try {
        // Sign up with Supabase
        // The database trigger automatically creates the user record in public.users
        const { data: authData, error: authError } = await supabase.auth.signUp({
          email,
          password,
          options: {
            data: {
              name,
              phone,
              // Default registration type - not driver or company admin
              registerAsDriver: false,
              registerAsCompanyAdmin: false,
            },
          },
        });

        if (authError) {
          throw new Error(authError.message);
        }

        if (!authData.user) {
          throw new Error("Registration failed");
        }

        const supabaseSession = authData.session;

        // If email confirmation is required, we'll get a user but no session
        if (!supabaseSession) {
          setLoading(false);
          return {
            success: true,
            requiresEmailConfirmation: true,
          };
        }

        // Fetch user profile and memberships from our API
        // The user record was created by the database trigger during signup
        const response = await api.get<ApiResponse<AuthSession>>(
          "/auth/session",
          {
            headers: {
              Authorization: `Bearer ${supabaseSession.access_token}`,
            },
          }
        );

        if (!response.data.success || !response.data.data) {
          throw new Error("Failed to fetch user profile");
        }

        const authSession = response.data.data;
        setAuth(authSession.user, authSession.memberships);
        surfaceLinkedRides(authSession);

        return { success: true };
      } catch (error) {
        setLoading(false);
        const message = error instanceof Error ? error.message : "Registration failed";
        return { success: false, error: message };
      }
    },
    [setAuth, setLoading]
  );

  const logout = useCallback(async () => {
    try {
      await supabase.auth.signOut();
    } finally {
      clearAuth();
      clearUpgradeDismissals();
    }
  }, [clearAuth]);

  const forgotPassword = useCallback(async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });

    if (error) {
      return { success: false, error: error.message };
    }

    return { success: true };
  }, []);

  const resetPassword = useCallback(async (newPassword: string) => {
    const { error } = await supabase.auth.updateUser({
      password: newPassword,
    });

    if (error) {
      return { success: false, error: error.message };
    }

    return { success: true };
  }, []);

  // Change password from the authenticated settings dialog.
  // Unlike resetPassword (which is for the email-link flow), this verifies the
  // user's current password server-side before allowing the update.
  const changePassword = useCallback(
    async (currentPassword: string, newPassword: string) => {
      try {
        await api.post("/users/me/change-password", { currentPassword, newPassword });
        return { success: true as const };
      } catch (error) {
        if (isAxiosError(error)) {
          const data = error.response?.data as
            | { error?: { message?: string } }
            | undefined;
          const message =
            data?.error?.message ??
            "Couldn't update your password. Please try again.";
          return { success: false as const, error: message };
        }
        return {
          success: false as const,
          error: "Couldn't update your password. Please try again.",
        };
      }
    },
    []
  );

  return {
    login,
    register,
    logout,
    forgotPassword,
    resetPassword,
    changePassword,
  };
}

/**
 * Hook to initialize auth state from existing session
 */
export function useAuthInitializer() {
  const { setAuth, clearAuth, setLoading } = useAuthStore();

  useEffect(() => {
    let mounted = true;

    const initializeAuth = async () => {
      try {
        // Get current session from Supabase
        const {
          data: { session },
        } = await supabase.auth.getSession();

        if (!session) {
          if (mounted) {
            clearAuth();
          }
          return;
        }

        // Fetch user profile and memberships
        const response = await api.get<ApiResponse<AuthSession>>("/auth/session", {
          headers: {
            Authorization: `Bearer ${session.access_token}`,
          },
        });

        if (mounted) {
          if (response.data.success && response.data.data) {
            const sessionData = response.data.data;
            setAuth(sessionData.user, sessionData.memberships);
            surfaceLinkedRides(sessionData);
          } else {
            // API returned success: false, but we have a Supabase session
            // Just mark loading as complete without clearing auth
            setLoading(false);
          }
        }
      } catch (error) {
        // Don't clear auth on API errors (e.g., rate limiting)
        // User may already be authenticated via LoginPage's setAuth call
        console.error("Failed to fetch session from API:", error);
        if (mounted) {
          setLoading(false);
        }
      }
    };

    void initializeAuth();

    // Listen for auth state changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (!mounted) return;

      if (event === "SIGNED_OUT" || !session) {
        clearAuth();
        return;
      }

      if (event === "SIGNED_IN" || event === "TOKEN_REFRESHED") {
        try {
          const response = await api.get<ApiResponse<AuthSession>>("/auth/session", {
            headers: {
              Authorization: `Bearer ${session.access_token}`,
            },
          });

          if (response.data.success && response.data.data) {
            const sessionData = response.data.data;
            setAuth(sessionData.user, sessionData.memberships);
            surfaceLinkedRides(sessionData);
          }
        } catch (error) {
          // Don't clear auth on API errors (e.g., rate limiting)
          // Only clear on explicit sign out
          console.error("Failed to fetch session from API:", error);
        }
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [setAuth, clearAuth, setLoading]);
}
